import { SubmitSuccessAction } from "@wix/ambassador-forms-v4-form/types";
import { submitFormClick } from "@wix/bi-logger-forms-data/v2";
import { useTranslation, useBi, useSubmitSuccessTrigger, usePanorama, useExperiments, EXPERIMENTS } from "@wix/form-fields";
import { useCallback } from "react";
import { useSubmitStatus } from "../../contexts/submit-status-context.js";
import { INTERACTIONS } from "../../features/monitoring/interactions.js";
import { BiFormSubmissionStatus } from "../../types/bi.js";
import { getCheckoutMessage, redirectToExternalUrl, getErrorCode, getErrorList, getFormCustomErrorMessage, getFieldCustomErrorMessages } from "./utils.js";
const useSubmit = ({
  validateAndScrollToError,
  submitAction,
  onSubmit,
  onSubmitSuccess,
  onSubmitFailure,
  resetFormValues,
  values,
  submitSettings,
  handleStepChange,
  navigateToCheckout,
  openPopup,
  onValidate,
  handleScrollToFirstError
}) => {
  const {
    setSubmitSuccessMessage,
    setSubmitFailureCode,
    setSubmitFailureMessage
  } = useSubmitStatus();
  const { t } = useTranslation();
  const { report } = useBi();
  const fireSubmitSuccess = useSubmitSuccessTrigger();
  const panorama = usePanorama();
  const { isExperimentEnabled } = useExperiments();
  const focusOnSubmitSuccess = !isExperimentEnabled(
    EXPERIMENTS.SUBMIT_SUCCESS_NO_FOCUS
  );
  const reportSubmitFormBi = useCallback(
    ({ status, id }) => report(
      submitFormClick({
        submission_status: status,
        submission_id: id ?? void 0
      })
    ),
    [report]
  );
  const handleSubmitSuccess = useCallback(
    (submission) => {
      const action = submitSettings?.submitSuccessAction;
      const checkoutId = submission?.orderDetails?.checkoutId;
      if (checkoutId && navigateToCheckout) {
        setSubmitSuccessMessage(
          getCheckoutMessage(t("submit.checkout.message"))
        );
        navigateToCheckout(checkoutId);
      } else if (action === SubmitSuccessAction.THANK_YOU_MESSAGE) {
        setSubmitSuccessMessage(
          submitSettings?.thankYouMessageOptions?.richContent
        );
        if (submitSettings?.thankYouMessageOptions?.durationInSeconds) {
          setTimeout(() => {
            setSubmitSuccessMessage(void 0);
            if (focusOnSubmitSuccess) {
              handleStepChange(0);
            }
          }, submitSettings?.thankYouMessageOptions?.durationInSeconds * 1e3);
        } else if (focusOnSubmitSuccess) {
          handleStepChange(0);
        }
      } else if (action === SubmitSuccessAction.REDIRECT) {
        redirectToExternalUrl(submitSettings?.redirectOptions);
      } else if (action === SubmitSuccessAction.POPUP) {
        setTimeout(() => {
          openPopup?.(submitSettings?.popupOptions?.popupId ?? "", {});
        }, 500);
      }
      setSubmitFailureCode(void 0);
      setSubmitFailureMessage(void 0);
    },
    [
      t,
      submitSettings,
      handleStepChange,
      focusOnSubmitSuccess,
      navigateToCheckout,
      openPopup,
      setSubmitSuccessMessage,
      setSubmitFailureCode,
      setSubmitFailureMessage
    ]
  );
  const handleSubmitFailure = useCallback(
    (error) => {
      const err = getErrorCode(error);
      setSubmitFailureCode(err);
      setSubmitSuccessMessage(void 0);
    },
    [setSubmitFailureCode, setSubmitSuccessMessage]
  );
  const handleCustomErrorMessages = useCallback(
    (error) => {
      const errors = getErrorList(error);
      const formCustomErrorMessage = getFormCustomErrorMessage(errors);
      if (formCustomErrorMessage) {
        setSubmitFailureMessage(formCustomErrorMessage);
      }
      const fieldCustomErrorMessages = getFieldCustomErrorMessages(errors);
      if (fieldCustomErrorMessages?.length) {
        onValidate(fieldCustomErrorMessages);
        handleScrollToFirstError?.(fieldCustomErrorMessages);
      }
    },
    [setSubmitFailureMessage, onValidate, handleScrollToFirstError]
  );
  const submitForm = useCallback(async () => {
    const errors = await validateAndScrollToError(values);
    onSubmit?.();
    reportSubmitFormBi({ status: BiFormSubmissionStatus.INIT });
    if (errors?.length) {
      reportSubmitFormBi({ status: BiFormSubmissionStatus.VALIDATION_FAILURE });
      return;
    }
    let response;
    try {
      panorama?.transaction(INTERACTIONS.SUBMIT_FORM).start();
      response = await submitAction(values);
    } catch (error) {
      onSubmitFailure?.(error);
      reportSubmitFormBi({ status: BiFormSubmissionStatus.FAILURE });
      handleCustomErrorMessages(error);
      handleSubmitFailure(error);
      throw error;
    }
    if (response) {
      onSubmitSuccess?.(response);
      reportSubmitFormBi({
        status: BiFormSubmissionStatus.SUCCESS,
        id: response.id
      });
      panorama?.transaction(INTERACTIONS.SUBMIT_FORM).finish();
      fireSubmitSuccess({ ...values });
      handleSubmitSuccess(response);
      resetFormValues?.();
    }
    return response;
  }, [
    validateAndScrollToError,
    values,
    onSubmit,
    reportSubmitFormBi,
    panorama,
    submitAction,
    onSubmitFailure,
    handleCustomErrorMessages,
    handleSubmitFailure,
    onSubmitSuccess,
    fireSubmitSuccess,
    handleSubmitSuccess,
    resetFormValues
  ]);
  return { submitForm };
};
export {
  useSubmit
};
//# sourceMappingURL=use-submit.js.map
